import {resolveAuthorImage} from './blogAuthors';
import {DEFAULT_REVIEWER} from './reviewer';

/**
 * `use_case` posts carry no byline in WordPress (no `author_info` / `reviewer_info` in REST), so
 * the author comes from the post's categories instead: AI topics → Gurpreet Singh, blockchain
 * topics → Daljit Singh. Both are reviewed by the blog's default reviewer. Profile text mirrors
 * their WordPress `author_info` on blog posts; photos are the CDN copies the blog already uses.
 * Once WordPress exposes `author_info` for use cases, that should win over this map.
 */
export type UseCasePerson = {
    name: string;
    slug: string;
    designation: string;
    bio: string;
    image: string;
    linkedin: string;
};

const GURPREET_SINGH: UseCasePerson = {
    name: 'Gurpreet Singh',
    slug: 'gurpreet-singh',
    designation: 'CEO & Director of AI & Emerging Technologies',
    bio: 'A technology leader with 28 years of experience, specializing in AI consulting, business transformation, and enterprise innovation. Works with CXOs to prioritize high-value AI use cases, assess readiness, and shape responsible roadmaps across generative AI, machine learning, NLP, and computer vision.',
    image: resolveAuthorImage(undefined, 'Gurpreet Singh'),
    linkedin: 'https://www.linkedin.com/in/gurpreet-singh-630b5a12/'
};

const DALJIT_SINGH: UseCasePerson = {
    name: 'Daljit Singh',
    slug: 'daljit_singh',
    designation: 'Co-founder & Director of Blockchain & AI Technology',
    bio: 'Combines 25+ years of enterprise engineering and product delivery experience with hands-on leadership across AI, Blockchain, Web3, FinTech, HealthTech, Supply Chain, and SaaS, helping organizations turn complex concepts into scalable, production-ready digital platforms.',
    image: resolveAuthorImage(undefined, 'Daljit Singh'),
    linkedin: 'https://www.linkedin.com/in/daljit-singh-panesar-522b3918/'
};

/** Reviewer of every use case that has an author. */
export const USE_CASE_REVIEWER: UseCasePerson = DEFAULT_REVIEWER;

// Category slugs as they are in WordPress (`use_cases_categories`).
const BLOCKCHAIN_SLUGS: ReadonlySet<string> = new Set(['blockchain', 'tokenization']);
const AI_SLUGS: ReadonlySet<string> = new Set(['artificial-intelligence', 'generative-ai']);

/**
 * The author for a use case, from ALL its category slugs (not just the one its label shows), so
 * the listing and the detail page always agree. A use case in a blockchain topic goes to Daljit
 * even if it is also tagged AI; one in neither group gets no byline rather than a guessed one.
 */
export const resolveUseCaseAuthor = (categorySlugs: ReadonlyArray<string | undefined>): UseCasePerson | undefined => {
    if (categorySlugs.some((slug) => slug && BLOCKCHAIN_SLUGS.has(slug))) return DALJIT_SINGH;
    if (categorySlugs.some((slug) => slug && AI_SLUGS.has(slug))) return GURPREET_SINGH;
    return undefined;
};
